import { currentStoreView } from '@vue-storefront/core/lib/multistore'
import { isServer } from './isServer'
import config from 'config'

function stripTrailingSlash (s: string): string {
  return s.replace(/\/$/, '')
}

/** Browser / SSR app origin for same-origin Magento REST (avoids cross-origin CORS preflight). */
export function appOriginForRest (): string {
  if (!isServer && typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin
  }
  const s = (config as any).server
  if (s?.host) {
    const proto = s.protocol || 'http'
    const port = s.port && String(s.port) !== '80' && String(s.port) !== '443' ? `:${s.port}` : ''
    return `${proto}://${s.host}${port}`
  }
  return ''
}

/**
 * Base URL for Magento REST: segment before `/V1` (requests use `${base}/V1/...`).
 *
 * 1. `magentoApi.endpoint` — absolute `https://host/rest/.../V1` (needs Magento CORS for other
 *    origins) or relative `/rest/.../V1` (same-origin; use `magentoApi.proxyTarget` on the Node
 *    server to forward `/rest` to Magento in dev).
 * 2. Else `magento2.api.url` or `magento2.url` + `/rest`, plus optional multistore path segment.
 */
export function magentoRestBaseUrl (): string {
  const magentoApiEp = (config as any).magentoApi?.endpoint
  if (magentoApiEp) {
    let ep = stripTrailingSlash(String(magentoApiEp))
    if (/\/v1$/i.test(ep)) {
      ep = ep.slice(0, -3)
    }
    // /rest/all/ creates quotes on admin store (id 0); Magento 2.4+ then rejects every product.
    const { storeCode } = currentStoreView()
    const sv = (config as any).storeViews
    if (storeCode && sv?.multistore && /\/rest\/all\b/i.test(ep)) {
      ep = ep.replace(/\/rest\/all\b/i, `/rest/${storeCode}`)
    }
    if (ep.startsWith('/')) {
      const origin = appOriginForRest()
      if (!origin) return ''
      return `${origin}${ep}`
    }
    return ep
  }

  const m2 = (config as any).magento2 || {}
  const fromApi = m2.api?.url && stripTrailingSlash(String(m2.api.url))
  let base = fromApi || ''
  if (!base && m2.url) {
    base = `${stripTrailingSlash(String(m2.url))}/rest`
  }
  if (!base) return ''
  const { storeCode } = currentStoreView()
  const sv = (config as any).storeViews
  if (sv?.multistore && storeCode && sv[storeCode] && typeof sv[storeCode] === 'object') {
    base = `${base}/${storeCode}`
  }
  return base
}

/** Magento REST V1 base, e.g. `https://www.zaun24.at/rest/zaun24_at/V1`. */
export function magentoRestV1BaseUrl (): string {
  const base = magentoRestBaseUrl()
  return base ? `${base}/V1` : ''
}
